import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  TableSortLabel,
  Checkbox,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoIcon from '@mui/icons-material/Info';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import WarningIcon from '@mui/icons-material/Warning';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const FortnoxInvoiceTable = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    invoices: [],
    metaInfo: {}
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('feedback');
  const [selectedRows, setSelectedRows] = useState({});
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [sentNumbers, setSentNumbers] = useState(new Set());
  const [sendingResults, setSendingResults] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customersToSend, setCustomersToSend] = useState([]);
  const [deduplicatedCustomers, setDeduplicatedCustomers] = useState([]);
  const [skippedCustomers, setSkippedCustomers] = useState([]);
  const [existingRequestCustomers, setExistingRequestCustomers] = useState([]);
  const [alreadySentCustomers, setAlreadySentCustomers] = useState([]);
  const [sendProgress, setSendProgress] = useState({
    total: 0,
    current: 0,
    successful: 0,
    failed: 0,
    completed: false
  });
  const [debugInfo, setDebugInfo] = useState(null);
  const [sendingMultiple, setSendingMultiple] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const fetchInvoicesWithCustomers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/fortnox/invoices-with-feedback-status');
      setData({
        invoices: response.data.InvoicesWithCustomers,
        metaInfo: response.data.MetaInfo || {}
      });
      setFilteredInvoices(response.data.InvoicesWithCustomers);
      
      // Reset selections on data refresh
      setSelectedRows({});
      setSelectAllChecked(false);
    } catch (err) {
      console.error('Error fetching Fortnox data:', err);
      
      // Check if this is a token expiration error
      const errorResponse = err.response?.data;
      if (errorResponse?.error_code === 'token_expired' || 
          (errorResponse?.detail && errorResponse.detail.includes('token')) ||
          (err.message && err.message.toLowerCase().includes('token'))) {
        
        // Handle token expiration by triggering a reconnection
        setError('Din anslutning till Fortnox har gått ut (OAuth-tokens gäller i 45 dagar). Vänligen förnya din anslutning.');
        
        // Show a dialog to the user if present
        if (typeof window !== 'undefined' && window.confirm('Din anslutning till Fortnox har gått ut. Vill du förnya din anslutning nu?')) {
          // Reload the page to show the connection UI
          window.location.href = "/fortnox?active_tab=fortnox";
        }
      } else {
        setError('Ett fel uppstod vid hämtning av data från Fortnox: ' + (err.response?.data?.detail || err.message || 'Okänt fel'));
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (invoices, query) => {
    if (!query) {
      setFilteredInvoices(invoices);
      return;
    }
    
    const lowercaseQuery = query.toLowerCase();
    const filtered = invoices.filter(invoice => {
      // Check if any of these fields contain the search query
      return (
        (invoice.DocumentNumber && invoice.DocumentNumber.toLowerCase().includes(lowercaseQuery)) ||
        (invoice.CustomerName && invoice.CustomerName.toLowerCase().includes(lowercaseQuery)) ||
        (invoice.CustomerInfo?.Name && invoice.CustomerInfo.Name.toLowerCase().includes(lowercaseQuery)) ||
        (invoice.CustomerNumber && invoice.CustomerNumber.toLowerCase().includes(lowercaseQuery)) ||
        (invoice.CustomerInfo?.Phone && invoice.CustomerInfo.Phone.toLowerCase().includes(lowercaseQuery)) ||
        (invoice.CustomerInfo?.Mobile && invoice.CustomerInfo.Mobile.toLowerCase().includes(lowercaseQuery))
      );
    });
    
    setFilteredInvoices(filtered);
  };

  // Apply filter when search query changes
  useEffect(() => {
    applyFilter(data.invoices, searchQuery);
  }, [searchQuery, data.invoices]);

  // Load data when component mounts
  useEffect(() => {
    fetchInvoicesWithCustomers();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchInvoicesWithCustomers();
  };

  // Determine invoice status from available fields
  const determineInvoiceStatus = (invoice) => {
    if (invoice.Cancelled) {
      return 'CANCELLED';
    }
    
    if (!invoice.Booked) {
      return 'DRAFT';
    }
    
    // Invoice is booked
    if (invoice.Balance <= 0 && invoice.FinalPayDate) {
      return 'PAID';
    }
    
    // Unpaid but due date has passed
    if (invoice.Balance > 0 && new Date(invoice.DueDate) < new Date()) {
      return 'OVERDUE';
    }
    
    // Unpaid and due date has not passed yet
    if (invoice.Balance > 0) {
      return 'UNPAID';
    }
    
    // Default case
    return 'ONGOING';
  };

  // Get status color based on invoice status
  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'DONE':
      case 'PAID':
      case 'FINISHED':
        return 'success';
      case 'DRAFT':
      case 'ONGOING':
      case 'FACTORING':
      case 'REMINDER':
      case 'WAITING':
        return 'warning';
      case 'UNPAID':
      case 'CANCELLED':
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  // Translate Fortnox invoice status to Swedish
  const translateInvoiceStatus = (status) => {
    if (!status) return 'Okänd';
    
    const statusMap = {
      'APPROVED': 'Godkänd',
      'DRAFT': 'Utkast',
      'ONGOING': 'Pågående',
      'FACTORING': 'Fakturaköp',
      'REMINDER': 'Påminnelse',
      'WAITING': 'Väntar',
      'UNPAID': 'Obetald',
      'PAID': 'Betald',
      'FINISHED': 'Slutförd',
      'CANCELLED': 'Avbruten',
      'OVERDUE': 'Förfallen',
      'DONE': 'Klar'
    };
    
    return statusMap[status] || status;
  };

  // Format currency with SEK suffix
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return `${Number(amount).toLocaleString('sv-SE')} SEK`;
  };

  // Format date to Swedish format
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('sv-SE');
    } catch (e) {
      return dateStr;
    }
  };

  // Handle row selection
  const handleRowSelect = (event, invoice) => {
    event.stopPropagation(); // Prevent row click opening the dialog
    
    // Only allow selection of rows with mobile numbers AND without existing feedback requests
    if (invoice.CustomerInfo?.MobileNumbersCount > 0 && !invoice.HasFeedbackRequest) {
      setSelectedRows(prev => {
        const newSelected = { ...prev };
        if (newSelected[invoice.DocumentNumber]) {
          delete newSelected[invoice.DocumentNumber];
        } else {
          newSelected[invoice.DocumentNumber] = invoice;
        }
        return newSelected;
      });
    }
  };
  
  // Handle select all checkbox
  const handleSelectAllClick = (event) => {
    event.stopPropagation();
    
    if (selectAllChecked) {
      // Deselect all
      setSelectedRows({});
      setSelectAllChecked(false);
    } else {
      // Select all eligible rows (has mobile number AND no existing feedback request)
      const newSelected = {};
      filteredInvoices
        .filter(invoice => invoice.CustomerInfo?.MobileNumbersCount > 0 && !invoice.HasFeedbackRequest)
        .forEach(invoice => {
          newSelected[invoice.DocumentNumber] = invoice;
        });
      setSelectedRows(newSelected);
      setSelectAllChecked(true);
    }
  };
  
  // Get count of selected rows
  const selectedCount = Object.keys(selectedRows).length;
  
  // Check if a row is selected
  const isSelected = (documentNumber) => !!selectedRows[documentNumber];
  
  // Format a phone number to +46 format
  const formatPhoneNumber = (phone) => {
    if (!phone) return null;
    
    // Remove all non-digit characters
    let digits = phone.replace(/\D/g, '');
    
    // Handle different Swedish number formats
    if (digits.startsWith('0046')) {
      // Convert 0046 to +46
      return '+' + digits.substring(2);
    } else if (digits.startsWith('46')) {
      // Add + to 46
      return '+' + digits;
    } else if (digits.startsWith('07')) {
      // Convert Swedish mobile starting with 0 to +46
      return '+46' + digits.substring(1);
    } else if (digits.startsWith('7') && digits.length >= 9) {
      // Assume it's a Swedish mobile without country code
      return '+46' + digits;
    }
    
    // Return original if we couldn't format it
    return phone;
  };
  
  // Check if a phone number is valid for sending
  const isValidPhoneNumber = (phone) => {
    if (!phone) return false;
    
    // Format the phone number
    const formattedPhone = formatPhoneNumber(phone);
    
    // Check if it starts with +46 and has enough digits (country code + 9 digits)
    return formattedPhone?.startsWith('+46') && formattedPhone.length >= 12;
  };
  
  // Reset sent numbers tracking when data is refreshed
  useEffect(() => {
    setSentNumbers(new Set());
    setSendingResults(null);
  }, [data.invoices]);
  
  // Handle sending feedback request to a single invoice
  const handleSendFeedbackRequest = async (invoice, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Confirm before sending
    if (!window.confirm(`Vill du skicka en recensionsförfrågan till ${invoice.CustomerInfo?.Name || 'kunden'}?`)) {
      return;
    }
    
    setSendingFeedback(true);
    setDebugInfo(invoice);
    
    try {
      // Validate we have a mobile number
      const mobilesToSend = invoice.CustomerInfo?.PrimaryMobile;
      
      if (!mobilesToSend || !isValidPhoneNumber(mobilesToSend)) {
        throw new Error(`Inget giltigt mobilnummer hittades för ${invoice.CustomerInfo?.Name || 'kunden'}`);
      }
      
      // Add debug logging
      console.log('DEBUG - Before formatting:', mobilesToSend);
      console.log('DEBUG - Before formatting type:', typeof mobilesToSend);
      
      // Format the phone number to E.164 format
      const formattedPhone = formatPhoneNumber(mobilesToSend);
      console.log('DEBUG - Original phone:', mobilesToSend);
      console.log('DEBUG - Formatted phone:', formattedPhone);
      console.log('DEBUG - Formatted phone type:', typeof formattedPhone);
      console.log('DEBUG - Formatted phone length:', formattedPhone.length);
      
      // Prepare request data - phone number at root level
      const requestData = {
        number: formattedPhone,
        customer_name: invoice.CustomerInfo?.Name || 'Kund'
      };
      console.log('DEBUG - Request data:', requestData);
      console.log('DEBUG - Request data stringified:', JSON.stringify(requestData));
      
      // Send feedback request
      const response = await api.post('/api/feedback/initiate-feedback', requestData);
      console.log('DEBUG - Response:', response.data);
      
      if (response.data && response.data.id) {
        alert(`Recensionsförfrågan skickad till ${invoice.CustomerInfo?.Name || 'kunden'}`);
        // Refresh the data to update the status
        await fetchInvoicesWithCustomers();
      } else {
        throw new Error('Kunde inte skicka förfrågan');
      }
      
    } catch (error) {
      console.error('Error sending feedback request:', error);
      if (error.response?.data?.detail) {
        console.error('Validation error details:', error.response.data.detail);
      }
      alert('Fel vid skickande av recensionsförfrågan: ' + (error.response?.data?.detail || error.message || 'Okänt fel'));
    } finally {
      setSendingFeedback(false);
      if (debugOpen) {
        setDebugOpen(false);
      }
    }
  };
  
  // Prepare customers for sending and open confirmation dialog
  const prepareToSendMultipleFeedbackRequests = () => {
    // Confirm with user
    if (selectedCount === 0) {
      alert('Inga kunder valda. Välj kunder genom att kryssa i rutorna i tabellen.');
      return;
    }
    
    // Clear previous data
    const toSend = [];
    const skipped = [];
    const existing = [];
    const alreadySent = [];
    
    // Check each selected invoice
    Object.values(selectedRows).forEach(invoice => {
      const primaryMobile = invoice.CustomerInfo?.PrimaryMobile;
      
      // Skip if no valid mobile
      if (!primaryMobile || !isValidPhoneNumber(primaryMobile)) {
        skipped.push({
          name: invoice.CustomerInfo?.Name || 'Okänd kund',
          reason: 'Inget giltigt mobilnummer'
        });
        return;
      }
      
      // Format the number properly
      const formattedPhone = formatPhoneNumber(primaryMobile);
      
      // Check if it's been sent in this session
      if (sentNumbers.has(formattedPhone)) {
        alreadySent.push({
          name: invoice.CustomerInfo?.Name || '',
          documentNumber: invoice.DocumentNumber || '',
          phoneNumber: formattedPhone
        });
        return;
      }
      
      // Note if it already has an existing request
      if (invoice.HasFeedbackRequest) {
        existing.push({
          name: invoice.CustomerInfo?.Name || '',
          documentNumber: invoice.DocumentNumber || '',
          phoneNumber: formattedPhone
        });
      }
      
      // Add to customers to send
      toSend.push({
        phoneNumber: formattedPhone,
        customerName: invoice.CustomerInfo?.Name || '',
        customerNumber: invoice.CustomerNumber || '',
        documentNumber: invoice.DocumentNumber || ''
      });
    });
    
    // If no valid customers to send to, show warning
    if (toSend.length === 0) {
      alert('Inga giltiga kunder att skicka till. Kontrollera mobilnummer.');
      return;
    }

    // Now deduplicate the customers by phone number
    const phoneNumberMap = new Map();
    
    // Group customers by phone number
    toSend.forEach(customer => {
      if (!phoneNumberMap.has(customer.phoneNumber)) {
        phoneNumberMap.set(customer.phoneNumber, [customer]);
      } else {
        phoneNumberMap.get(customer.phoneNumber).push(customer);
      }
    });
    
    // Create deduplicated list (one entry per phone number)
    const deduplicated = Array.from(phoneNumberMap.entries()).map(([phoneNumber, customers]) => {
      // If multiple customers share this phone number, create a combined entry
      if (customers.length > 1) {
        return {
          phoneNumber,
          customerName: customers.map(c => c.customerName).join(', '),
          customerNumbers: customers.map(c => c.customerNumber),
          documentNumbers: customers.map(c => c.documentNumber),
          isDuplicate: true,
          count: customers.length
        };
      } else {
        // Just return the single customer
        return {
          ...customers[0],
          isDuplicate: false,
          count: 1
        };
      }
    });
    
    // Update state with all the customer groups
    setCustomersToSend(toSend);
    setDeduplicatedCustomers(deduplicated);
    setSkippedCustomers(skipped);
    setExistingRequestCustomers(existing);
    setAlreadySentCustomers(alreadySent);
    
    // Open the confirmation dialog
    setConfirmDialogOpen(true);
  };
  
  // Handle closing the confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };
  
  // Handle the actual sending after confirmation
  const handleSendMultipleFeedbackRequests = async () => {
    setSendingMultiple(true);
    setConfirmDialogOpen(false);
    
    try {
      // Track successful and failed sends
      const successful = [];
      const failed = [];
      
      // Create a copy of the deduped data for tracking progress
      const totalToSend = deduplicatedCustomers.length;
      let currentProgress = 0;
      
      // Process each customer
      for (const customer of deduplicatedCustomers) {
        try {
          // Send the request
          const response = await api.post('/api/feedback/initiate-feedback', {
            phone: {
              number: customer.phoneNumber,
              customer_name: customer.customerName || 'Kund'
            }
          });
          
          if (response.data && response.data.id) {
            successful.push(customer);
            // Add to sent numbers set
            sentNumbers.add(customer.phoneNumber);
          } else {
            failed.push({
              ...customer,
              error: 'Kunde inte skicka förfrågan'
            });
          }
        } catch (error) {
          console.error('Error sending to customer:', customer, error);
          failed.push({
            ...customer,
            error: error.response?.data?.detail || error.message || 'Okänt fel'
          });
        }
        
        // Update progress
        currentProgress++;
        setSendProgress(prev => ({
          ...prev,
          current: currentProgress,
          total: totalToSend,
          successful: successful.length,
          failed: failed.length
        }));
      }
      
      // Update results
      setSendingResults({
        successful,
        failed,
        skipped: skippedCustomers,
        existing: existingRequestCustomers,
        alreadySent: alreadySentCustomers
      });
      
      // Refresh the data
      await fetchInvoicesWithCustomers();
      
    } catch (error) {
      console.error('Error in batch sending:', error);
      alert('Ett fel uppstod vid skickande av förfrågningar: ' + (error.response?.data?.detail || error.message || 'Okänt fel'));
    } finally {
      setSendingMultiple(false);
      setSendProgress({
        total: 0,
        current: 0,
        successful: 0,
        failed: 0,
        completed: false
      });
    }
  };

  // Render function for feedback status
  const renderFeedbackStatus = (invoice) => {
    // Disable checkbox if no mobile number or if it already has feedback
    const disabled = invoice.CustomerInfo?.MobileNumbersCount === 0 || invoice.HasFeedbackRequest;
    let title = "Välj för att skicka recensionsförfrågan";
    
    if (invoice.CustomerInfo?.MobileNumbersCount === 0) {
      title = "Ingen mobiltelefon tillgänglig";
    } else if (invoice.HasFeedbackRequest) {
      title = "Recensionsförfrågan redan skickad";
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {!invoice.HasFeedbackRequest ? (
          <Checkbox 
            checked={isSelected(invoice.DocumentNumber)}
            onChange={(event) => handleRowSelect(event, invoice)}
            onClick={(event) => event.stopPropagation()}
            disabled={disabled || sendingFeedback}
            size="small"
            color="primary"
            title={title}
          />
        ) : (
          <Chip 
            label="Ja" 
            size="small" 
            color="success"
            variant="filled"
            sx={{ 
              fontSize: '0.7rem', 
              height: '20px',
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        )}
      </Box>
    );
  };

  // Handle row click to show debug information
  const handleRowClick = (invoice) => {
    setSelectedInvoice(invoice);
    setDebugOpen(true);
  };

  // Handle close debug dialog
  const handleCloseDebug = () => {
    setDebugOpen(false);
  };

  // Define column widths for better space utilization
  const columnWidths = {
    feedback: '12%',
    mobile: '13%',
    customer: '18%',
    docNumber: '8%',
    amount: '10%',
    date: '9%',
    dueDate: '10%',
    status: '10%'
  };

  // Format mobile number display
  const formatMobileNumber = (invoice) => {
    const mobileCount = invoice.CustomerInfo?.MobileNumbersCount || 0;
    const primaryMobile = invoice.CustomerInfo?.PrimaryMobile || '-';
    
    if (mobileCount === 0) {
      return (
        <Typography variant="body2" sx={{ fontSize: '0.825rem', color: 'text.secondary' }} noWrap>
          -
        </Typography>
      );
    } else if (mobileCount === 1) {
      return (
        <Typography variant="body2" sx={{ fontSize: '0.825rem' }} noWrap>
          {primaryMobile}
        </Typography>
      );
    } else {
      // Display badge with count of multiple numbers
      return (
        <Badge 
          badgeContent={mobileCount} 
          color="primary"
          sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: '0.825rem', mr: 0.5 }} noWrap>
              {primaryMobile}
            </Typography>
            <Tooltip title={`${mobileCount} mobilnummer`}>
              <PhoneIcon fontSize="small" sx={{ fontSize: '1rem', opacity: 0.7 }} />
            </Tooltip>
          </Box>
        </Badge>
      );
    }
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Sort function
  const sortInvoices = (array) => {
    if (!orderBy) return array;
    
    return array.sort((a, b) => {
      // Special case for feedback status (boolean)
      if (orderBy === 'feedback') {
        if (order === 'asc') {
          return a.HasFeedbackRequest === b.HasFeedbackRequest ? 0 : a.HasFeedbackRequest ? 1 : -1;
        } else {
          return a.HasFeedbackRequest === b.HasFeedbackRequest ? 0 : a.HasFeedbackRequest ? -1 : 1;
        }
      }
      // For other fields we could add additional sorting logic here
      return 0;
    });
  };

  // Apply filter and sort
  useEffect(() => {
    if (!data.invoices.length) {
      setFilteredInvoices([]);
      return;
    }
    
    // First apply filter
    let filtered = data.invoices;
    if (searchQuery) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = data.invoices.filter(invoice => {
        return (
          (invoice.DocumentNumber && invoice.DocumentNumber.toLowerCase().includes(lowercaseQuery)) ||
          (invoice.CustomerName && invoice.CustomerName.toLowerCase().includes(lowercaseQuery)) ||
          (invoice.CustomerInfo?.Name && invoice.CustomerInfo.Name.toLowerCase().includes(lowercaseQuery)) ||
          (invoice.CustomerNumber && invoice.CustomerNumber.toLowerCase().includes(lowercaseQuery)) ||
          (invoice.CustomerInfo?.PrimaryMobile && invoice.CustomerInfo.PrimaryMobile.toLowerCase().includes(lowercaseQuery))
        );
      });
    }
    
    // Then apply sort
    filtered = sortInvoices([...filtered]);
    
    setFilteredInvoices(filtered);
  }, [searchQuery, data.invoices, order, orderBy]);

  // Clear sending results
  const handleClearResults = () => {
    setSendingResults(null);
  };

  const handleSendReviewRequest = async (invoice) => {
    try {
      setLoading(true);
      const requestData = {
        number: invoice.CustomerInfo?.PrimaryMobile,
        customer_name: invoice.CustomerName
      };
      console.log('Sending review request with data:', requestData);
      
      const response = await fetch('/api/feedback/initiate-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.detail || 'Failed to send review request');
      }

      // Refresh the invoices list
      await fetchInvoicesWithCustomers();
    } catch (error) {
      console.error('Error sending review request:', error);
      setError('Failed to send review request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Fakturor från Fortnox
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <TextField
            label="Sök"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button 
            startIcon={<ReplayIcon />} 
            onClick={handleRefresh} 
            variant="outlined"
            disabled={loading}
          >
            Uppdatera
          </Button>
        </Box>
        
        {/* Multi-send feedback UI */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {selectedCount > 0 ? 
                `${selectedCount} kunder valda för recensionsförfrågan` : 
                'Välj kunder genom att kryssa i rutorna i tabellen'}
            </Typography>
            {selectedCount > 0 && (
              <Button 
                variant="text" 
                size="small" 
                onClick={() => setSelectedRows({})}
              >
                Rensa val
              </Button>
            )}
          </Stack>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={sendingFeedback ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={selectedCount === 0 || sendingFeedback}
            onClick={prepareToSendMultipleFeedbackRequests}
            size="medium"
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 'normal'
            }}
          >
            {sendingFeedback ? 'Skickar...' : `Skicka till ${selectedCount} kunder`}
          </Button>
        </Box>
        
        {/* Display send results if available */}
        {sendingResults && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            onClose={handleClearResults}
            action={
              <Button color="inherit" size="small" onClick={handleClearResults}>
                Stäng
              </Button>
            }
          >
            <Typography variant="subtitle2" gutterBottom>
              Skickade recensionsförfrågningar
            </Typography>
            <Typography variant="body2">
              {`Skickade: ${sendingResults.successful.length} av ${sendingResults.successful.length + sendingResults.failed.length}`}
            </Typography>
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Visar {filteredInvoices.length} av {data.metaInfo.TotalInvoices || 0} fakturor
              {data.metaInfo.TotalFeedbackRequests && (
                <> • {data.metaInfo.TotalFeedbackRequests} recensionsförfrågningar</>
              )}
            </Typography>
            
            <TableContainer>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: columnWidths.feedback, py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                          indeterminate={selectedCount > 0 && selectedCount < filteredInvoices.filter(i => i.CustomerInfo?.MobileNumbersCount > 0).length}
                          checked={selectAllChecked && selectedCount > 0}
                          onChange={handleSelectAllClick}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        <TableSortLabel
                          active={orderBy === 'feedback'}
                          direction={orderBy === 'feedback' ? order : 'asc'}
                          onClick={() => handleRequestSort('feedback')}
                        >
                          Recensionsförfrågan
                        </TableSortLabel>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: columnWidths.mobile, py: 1 }}>Mobilnummer</TableCell>
                    <TableCell sx={{ width: columnWidths.customer, py: 1 }}>Kund</TableCell>
                    <TableCell sx={{ width: columnWidths.docNumber, py: 1 }}>Fakturanr</TableCell>
                    <TableCell sx={{ width: columnWidths.amount, py: 1 }}>Belopp</TableCell>
                    <TableCell sx={{ width: columnWidths.date, py: 1 }}>Datum</TableCell>
                    <TableCell sx={{ width: columnWidths.dueDate, py: 1 }}>Förfallodatum</TableCell>
                    <TableCell sx={{ width: columnWidths.status, py: 1 }}>Status</TableCell>
                    <TableCell sx={{ width: '60px', py: 1 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!filteredInvoices.length && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Inga fakturor hittades
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {filteredInvoices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((invoice) => (
                      <TableRow 
                        key={invoice.DocumentNumber}
                        onClick={() => handleRowClick(invoice)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                          height: '40px', // Reduce row height
                          ...(isSelected(invoice.DocumentNumber) ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {})
                        }}
                        title="Klicka för att se detaljer"
                        selected={isSelected(invoice.DocumentNumber)}
                      >
                        <TableCell sx={{ py: 0.5 }}>
                          {renderFeedbackStatus(invoice)}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {formatMobileNumber(invoice)}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.825rem', fontWeight: 'medium' }}>
                            {invoice.CustomerInfo?.Name || invoice.CustomerName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            {invoice.DocumentNumber}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            {formatCurrency(invoice.Total)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            {formatDate(invoice.InvoiceDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                            {formatDate(invoice.DueDate)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip 
                            label={translateInvoiceStatus(determineInvoiceStatus(invoice))} 
                            size="small" 
                            color={getStatusColor(determineInvoiceStatus(invoice))} 
                            variant="outlined" 
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {!invoice.HasFeedbackRequest && invoice.CustomerInfo?.MobileNumbersCount > 0 && (
                            <Tooltip title="Visa detaljer">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(invoice);
                                }}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredInvoices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rader per sida:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} av ${count}`}
            />
          </>
        )}
      </Box>
      
      {/* Debug Dialog */}
      <Dialog 
        open={debugOpen} 
        onClose={handleCloseDebug}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Kundinformation: {selectedInvoice?.CustomerInfo?.Name || 'kund'}
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Kund: {selectedInvoice.CustomerInfo?.Name} (Fakturanr: {selectedInvoice.DocumentNumber})
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Mobilnummer:
              </Typography>
              
              {selectedInvoice.CustomerInfo?.MobileNumbers && 
              selectedInvoice.CustomerInfo.MobileNumbers.length > 0 ? (
                <List dense>
                  {selectedInvoice.CustomerInfo.MobileNumbers.map((mobile, index) => (
                    <ListItem key={`mobile-${index}`}>
                      <ListItemText 
                        primary={mobile}
                        secondary={index === 0 ? "Primärt mobilnummer" : "Alternativt mobilnummer"}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  Inga giltiga mobilnummer hittades för denna kund.
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Recensionsförfrågan:
              </Typography>
              
              <Alert 
                severity={selectedInvoice.HasFeedbackRequest ? "success" : "info"}
                sx={{ mb: 2 }}
              >
                {selectedInvoice.HasFeedbackRequest 
                  ? "En recensionsförfrågan har redan skickats till denna kund." 
                  : "Ingen recensionsförfrågan har skickats till denna kund ännu."}
              </Alert>
              
              {/* Only show Send Feedback Request button if there are mobile numbers, regardless of previous request */}
              {selectedInvoice.CustomerInfo?.MobileNumbers?.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    sx={{ mt: 1 }}
                    onClick={(e) => handleSendFeedbackRequest(selectedInvoice, e)}
                    disabled={sendingFeedback}
                    startIcon={sendingFeedback ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  >
                    {sendingFeedback ? 'Skickar...' : 'Skicka recensionsförfrågan'}
                  </Button>
                </Box>
              )}
              
              {/* Show advanced debugging section if desired */}
              {false && (
                <>
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Matchade telefonnummer i samtal:
                  </Typography>
                  
                  {selectedInvoice.FeedbackDebug?.matched_requests && selectedInvoice.FeedbackDebug.matched_requests.length > 0 ? (
                    <List dense>
                      {selectedInvoice.FeedbackDebug.matched_requests.map((phone, index) => (
                        <ListItem key={`match-${index}`}>
                          <ListItemText 
                            primary={phone} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="text.secondary">
                      Inga matchande telefonnummer hittades i samtalstabellen.
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDebug}>Stäng</Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={sendingFeedback ? null : handleCloseConfirmDialog} // Prevent closing during sending
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {sendingFeedback 
            ? `Skickar recensionsförfrågningar (${sendProgress.current}/${sendProgress.total})` 
            : 'Bekräfta recensionsförfrågningar'}
        </DialogTitle>
        <DialogContent>
          {!sendingFeedback ? (
            // Normal confirmation view when not sending
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', mt: 1 }}>
                {deduplicatedCustomers.length} recensionsförfrågningar kommer att skickas
              </Typography>
              
              {/* Show note if we deduplicated some entries */}
              {customersToSend.length > deduplicatedCustomers.length && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>OBS!</strong> {customersToSend.length - deduplicatedCustomers.length} dubbletter hittades (samma mobilnummer). Endast en förfrågan kommer att skickas per mobilnummer.
                  </Typography>
                </Alert>
              )}
              
              {/* Show list of deduplicated customers */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Följande kunder kommer att få en recensionsförfrågan:
              </Typography>
              
              <TableContainer sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kund</TableCell>
                      <TableCell>Mobilnummer</TableCell>
                      <TableCell>Fakturanr</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deduplicatedCustomers.map((customer, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {customer.isDuplicate ? (
                            <Tooltip title={`${customer.count} kunder med samma mobilnummer`}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', mr: 1 }}>
                                  {customer.customerName}
                                </Typography>
                                <Badge 
                                  badgeContent={customer.count} 
                                  color="primary"
                                  sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                                >
                                  <WarningIcon color="action" fontSize="small" />
                                </Badge>
                              </Box>
                            </Tooltip>
                          ) : (
                            customer.customerName
                          )}
                        </TableCell>
                        <TableCell>{customer.phoneNumber}</TableCell>
                        <TableCell>
                          {customer.isDuplicate ? 
                            customer.documentNumbers.join(', ') : 
                            customer.documentNumber
                          }
                        </TableCell>
                        <TableCell>
                          {existingRequestCustomers.some(c => c.phoneNumber === customer.phoneNumber) ? (
                            <Chip 
                              label="Tidigare skickad" 
                              size="small" 
                              color="warning"
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          ) : (
                            <Chip 
                              label="Ny" 
                              size="small" 
                              color="success"
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Show skipped and already sent customers if any */}
              {(skippedCustomers.length > 0 || alreadySentCustomers.length > 0) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Kunder som hoppas över:
                  </Typography>
                  
                  {skippedCustomers.length > 0 && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 1 }}>
                        {skippedCustomers.length} kunder utan giltigt mobilnummer:
                      </Typography>
                      <List dense>
                        {skippedCustomers.map((customer, index) => (
                          <ListItem key={`skipped-${index}`} dense>
                            <ListItemText
                              primary={customer.name}
                              secondary={customer.reason}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                  
                  {alreadySentCustomers.length > 0 && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 1 }}>
                        {alreadySentCustomers.length} kunder har redan fått en förfrågan i denna session:
                      </Typography>
                      <List dense>
                        {alreadySentCustomers.map((customer, index) => (
                          <ListItem key={`sent-${index}`} dense>
                            <ListItemText
                              primary={customer.name}
                              secondary={customer.phoneNumber}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            // Sending progress view
            <>
              <Box sx={{ width: '100%', my: 3, textAlign: 'center' }}>
                <CircularProgress 
                  variant="determinate" 
                  value={(sendProgress.current / sendProgress.total) * 100} 
                  size={80}
                  thickness={4}
                  sx={{ mb: 3 }}
                />
                
                <Typography variant="h6" gutterBottom>
                  {sendProgress.completed 
                    ? 'Alla förfrågningar skickade!' 
                    : `Skickar förfrågningar (${sendProgress.current} av ${sendProgress.total})`}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Lyckades</Typography>
                    <Typography variant="h6" color="success.main">{sendProgress.successful}</Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Misslyckades</Typography>
                    <Typography variant="h6" color="error.main">{sendProgress.failed}</Typography>
                  </Box>
                </Box>
                
                {sendProgress.error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {sendProgress.error}
                  </Alert>
                )}
                
                {sendProgress.completed && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Alla förfrågningar har skickats. Tabellen uppdateras automatiskt.
                  </Alert>
                )}
                
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {sendProgress.completed 
                    ? 'Stänger...' 
                    : 'Vänta medan recensionsförfrågningar skickas. Stäng inte dialogrutan.'}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!sendingFeedback ? (
            // Show normal buttons when not sending
            <>
              <Button onClick={handleCloseConfirmDialog}>
                Avbryt
              </Button>
              <Button 
                variant="contained" 
                onClick={handleSendMultipleFeedbackRequests} 
                color="primary"
                startIcon={<SendIcon />}
              >
                Skicka {deduplicatedCustomers.length} förfrågningar
              </Button>
            </>
          ) : (
            // Show different buttons during/after sending
            <>
              {sendProgress.completed ? (
                <Button 
                  onClick={handleCloseConfirmDialog}
                  variant="outlined"
                >
                  Stäng
                </Button>
              ) : (
                <Button 
                  disabled={true}
                  variant="outlined"
                >
                  Skickar...
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FortnoxInvoiceTable; 